var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import classnames from 'classnames';
import { Dropdown as CoreDropdown } from 'wix-ui-core/dist/es/src/components/dropdown';
import { TPAComponentsContext } from '../TPAComponentsConfig';
import { DropdownBase } from './DropdownBase';
import { DropdownOption } from './DropdownOption';
import { st, classes } from './Dropdown.st.css';
import { DATA_HOOKS } from './constants';
import { DropdownNativeSelect } from './DropdownNativeSelect';
import { deprecationLog, wrap, unwrap } from '../../common/deprecationLog';
import { OptionFactory, } from 'wix-ui-core/dist/es/src/components/dropdown-option';
import { modifyOptions } from './helpers';
import { IconTooltip } from '../IconTooltip';
import { TooltipSkin } from '../Tooltip/types';
import Error from 'wix-ui-icons-common/on-stage/Error';
import { ErrorMessageWrapper } from '../internal/ErrorMessageWrapper';
import { DropdownTheme, } from './types';
import { getAriaDescribedBy } from '../../common/a11y';
var uniqueId = require('lodash/uniqueId');
/**
 * A dropdown allows a user to select a value from a series of options.
 * Single selection dropdown.
 * */
var Dropdown = /** @class */ (function (_super) {
    __extends(Dropdown, _super);
    function Dropdown(props) {
        var _this = _super.call(this, props) || this;
        _this._rootRef = React.createRef();
        _this._dropdownRef = React.createRef();
        _this._nativeDropdownRef = React.createRef();
        _this.state = {
            selectedOption: null,
            ariaActivedescendant: null,
            isOpen: false,
            isErrorTooltipShown: false,
            focused: false,
        };
        _this.onSelect = function (selectedOption) {
            if (!selectedOption) {
                return;
            }
            var onChange = _this.props.onChange;
            _this.setState({
                selectedOption: selectedOption,
                isOpen: false,
                ariaActivedescendant: null,
            });
            onChange &&
                onChange(_this.props.options.find(function (_a) {
                    var id = _a.id;
                    return selectedOption.id === id;
                }));
        };
        _this.onOptionHover = function (option) {
            var ariaActivedescendant = option ? option._DOMid : null;
            _this.setState({ ariaActivedescendant: ariaActivedescendant });
        };
        _this.onExpandedChange = function (isOpen) {
            var onExpandedChange = _this.props.onExpandedChange;
            var newState = __assign({ isOpen: isOpen }, (!isOpen && { ariaActivedescendant: null }));
            _this.setState(newState);
            if (typeof onExpandedChange === 'function') {
                onExpandedChange(isOpen);
            }
        };
        _this.onCoreSelect = function (selectedCoreOption) {
            if (!selectedCoreOption) {
                return;
            }
            var selectedOption = _this.props.options.find(function (_a) {
                var id = _a.id;
                return selectedCoreOption.id === id;
            });
            _this.onSelect(selectedOption);
        };
        _this._toggleTooltip = function (showTooltip) {
            _this.setState({ isErrorTooltipShown: showTooltip });
        };
        _this._onErrorButtonClick = function (e) {
            var isErrorTooltipShown = _this.state.isErrorTooltipShown;
            _this._toggleTooltip(!isErrorTooltipShown);
            e.stopPropagation();
        };
        _this._onKeyDown = function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
            }
        };
        _this._onFocus = function () {
            var focused = _this.state.focused;
            if (!focused) {
                _this._toggleTooltip(true);
            }
            _this.setState({ focused: true });
        };
        _this._onBlur = function (e) {
            if (_this._rootRef.current &&
                // TODO: relatedTarget is an experimental feature, so remove this comment if it's no longer experimental or we've found a non-experimental solution
                !_this._rootRef.current.contains(e.relatedTarget)) {
                _this.setState({ focused: false });
                _this._toggleTooltip(false);
            }
        };
        _this._onErrorIconMouseDown = function () {
            _this.setState({ focused: true });
        };
        _this._getErrorIconTooltip = function () {
            var isErrorTooltipShown = _this.state.isErrorTooltipShown;
            var _a = _this.props, errorMessage = _a.errorMessage, errorTooltipPlacement = _a.errorTooltipPlacement;
            return (React.createElement(IconTooltip, { "data-hook": DATA_HOOKS.errorTooltip, className: classes.dropdownError, message: errorMessage, icon: React.createElement(Error, { className: classes.errorIcon }), tooltipId: _this._errorMessageId, tooltipSkin: TooltipSkin.Error, showTooltip: isErrorTooltipShown, onClick: _this._onErrorButtonClick, onMouseDown: _this._onErrorIconMouseDown, onKeyDown: _this._onKeyDown, tooltipProps: {
                    placement: errorTooltipPlacement,
                } }));
        };
        _this.renderNativeSelect = function () {
            var _a = _this.props, options = _a.options, placeholder = _a.placeholder, disabled = _a.disabled, error = _a.error, errorMessage = _a.errorMessage, ariaLabel = _a["aria-label"], ariaLabelledBy = _a["aria-labelledby"], name = _a.name, newErrorMessage = _a.newErrorMessage;
            return (React.createElement(DropdownNativeSelect, { "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, disabled: disabled, className: classes.dropdownNativeSelect, error: error, name: name, ref: _this._nativeDropdownRef, errorMessage: errorMessage, options: modifyOptions(options), onSelect: function (selectedOption) { return _this.onSelect(selectedOption); }, placeholder: placeholder, selectedOption: _this.state.selectedOption, errorComponent: _this._getErrorIconTooltip(), newErrorMessage: newErrorMessage }));
        };
        _this.renderCoreDropdown = function () {
            var _a;
            var _b = _this.props, placeholder = _b.placeholder, disabled = _b.disabled, error = _b.error, errorMessage = _b.errorMessage, options = _b.options, forceContentElementVisibility = _b.forceContentElementVisibility, placement = _b.placement, upgrade = _b.upgrade, ariaLabel = _b["aria-label"], ariaLabelledBy = _b["aria-labelledby"], flip = _b.flip, fixed = _b.fixed, appendTo = _b.appendTo, optionsContainerZIndex = _b.optionsContainerZIndex, optionsContainerClass = _b.optionsContainerClass, className = _b.className, ariaDescribedByProp = _b["aria-describedby"], alignment = _b.alignment, theme = _b.theme, dynamicWidth = _b.dynamicWidth, newErrorMessage = _b.newErrorMessage;
            var _c = _this.context, rtl = _c.rtl, isMobile = _c.mobile;
            var _d = _this.state, selectedOption = _d.selectedOption, ariaActivedescendant = _d.ariaActivedescendant, isOpen = _d.isOpen;
            var appendedToParent = !appendTo || appendTo === 'parent';
            var coreOptions = modifyOptions(options).map(function (option) {
                return option.divider
                    ? OptionFactory.createDivider({
                        className: classes.divider,
                    })
                    : OptionFactory.create(__assign(__assign({}, option), { render: function () { return (React.createElement(DropdownOption, __assign({ className: classes.dropdownOption }, option))); } }));
            });
            var ariaDescribedBy = getAriaDescribedBy([
                ariaDescribedByProp,
                errorMessage ? _this._errorMessageId : '',
            ]);
            return (React.createElement(CoreDropdown, { optionsContainerZIndex: optionsContainerZIndex, className: st(classes.root, { rtl: rtl, alignment: alignment, isMobile: isMobile, theme: theme }, classnames(classes.dropdown, optionsContainerClass, (_a = {},
                    /*
                      when dropdown is appended to a component other than parent,
                      we need to pass consumer's className to the core Dropdown in order for it to have affect on the portal
                    */
                    _a[className] = !appendedToParent,
                    _a))), contentClassName: classes.dropdownContainer, placement: placement, "data-hook": DATA_HOOKS.coreDropdown, "data-mobile": isMobile, options: coreOptions, onDeselect: _this.onCoreSelect, onSelect: _this.onCoreSelect, initialSelectedIds: selectedOption ? [selectedOption.id] : [], onInitialSelectedOptionsSet: function () { }, forceContentElementVisibility: forceContentElementVisibility, contentId: _this.contentId, openTrigger: disabled ? undefined : 'click', onExpandedChange: _this.onExpandedChange, onOptionHover: _this.onOptionHover, flip: flip, fixed: fixed, appendTo: appendTo, dynamicWidth: dynamicWidth || !appendedToParent, dropdownA11yFixes: true },
                React.createElement(DropdownBase, { className: classes.dropdownBase, selectedOption: selectedOption, placeholder: placeholder, disabled: disabled, error: error, upgrade: upgrade, ref: _this._dropdownRef, rtl: rtl, id: _this.labelForId, "aria-activedescendant": ariaActivedescendant, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, isExpanded: isOpen, "aria-describedby": ariaDescribedBy }),
                error &&
                    errorMessage &&
                    !newErrorMessage &&
                    _this._getErrorIconTooltip()));
        };
        wrap('Button');
        _this.contentId = props.optionsContainerId
            ? props.optionsContainerId
            : uniqueId('dropdown-options-container_');
        _this.labelForId = uniqueId('label-for-id_');
        _this._errorMessageId = uniqueId('error-message');
        return _this;
    }
    Dropdown.prototype.componentDidMount = function () {
        if (!this.props.upgrade) {
            deprecationLog('Dropdown', 'The current `Dropdown` component API will be deprecated in a future version. Please use the `upgrade` prop in order to use the new API.');
            unwrap('Button');
        }
    };
    Dropdown.getDerivedStateFromProps = function (nextProps, state) {
        if (state.selectedOption &&
            (!nextProps.initialSelectedId ||
                state.selectedOption.id === nextProps.initialSelectedId)) {
            return null;
        }
        return {
            selectedOption: nextProps.options.find(function (option) { return option.id === nextProps.initialSelectedId; }) || null,
        };
    };
    Dropdown.prototype.focus = function () {
        var _a, _b;
        if (((_a = this._dropdownRef) === null || _a === void 0 ? void 0 : _a.current) && !this.props.mobileNativeSelect) {
            this._dropdownRef.current.focus();
        }
        if (((_b = this._nativeDropdownRef) === null || _b === void 0 ? void 0 : _b.current) && this.props.mobileNativeSelect) {
            this._nativeDropdownRef.current.focus();
        }
    };
    Dropdown.prototype.shouldRenderNativeSelect = function () {
        var isMobile = this.context.mobile;
        return this.props.mobileNativeSelect && isMobile;
    };
    Dropdown.prototype.render = function () {
        var _a = this.props, label = _a.label, alignment = _a.alignment, className = _a.className, theme = _a.theme, newErrorMessage = _a.newErrorMessage, error = _a.error, errorMessage = _a.errorMessage;
        var _b = this.context, mobile = _b.mobile, rtl = _b.rtl;
        var Wrapper = newErrorMessage ? ErrorMessageWrapper : React.Fragment;
        var stStates = {
            alignment: alignment,
            mobile: mobile,
            theme: theme,
            rtl: rtl,
            newErrorMessage: newErrorMessage,
            error: error,
        };
        return (React.createElement(Wrapper, __assign({}, (newErrorMessage && {
            visible: error && !!errorMessage,
            message: errorMessage,
            messageId: this._errorMessageId,
            className: st(classes.root, stStates, className),
            'data-mobile': mobile,
            'data-hook': this.props['data-hook'],
        })),
            React.createElement("div", __assign({}, (!newErrorMessage && {
                className: st(classes.root, stStates, className),
                'data-mobile': mobile,
                'data-hook': this.props['data-hook'],
            }), { ref: this._rootRef, onFocus: this._onFocus, onBlur: this._onBlur }),
                label && (React.createElement("label", { "data-hook": DATA_HOOKS.label, htmlFor: this.labelForId, className: classes.label }, label)),
                this.shouldRenderNativeSelect()
                    ? this.renderNativeSelect()
                    : this.renderCoreDropdown())));
    };
    Dropdown.displayName = 'Dropdown';
    Dropdown.contextType = TPAComponentsContext;
    Dropdown.defaultProps = {
        placeholder: '',
        options: [],
        placement: 'bottom',
        mobileNativeSelect: false,
        upgrade: false,
        theme: DropdownTheme.Box,
    };
    return Dropdown;
}(React.Component));
export { Dropdown };
//# sourceMappingURL=Dropdown.js.map